// ============================================
// Cross-Document Context Builder (v4)
//
// Builds context blocks injected into generation
// prompts for non-TOS documents. Ensures every
// downstream document aligns with the TOS anchor
// on entities, defined terms, provision inheritance,
// and cross-references.
// ============================================

import { DEFINED_TERMS_REGISTRY } from '../../data/docops/definedTermsRegistry';
import { INHERITED_PROVISIONS } from '../../data/docops/provisionAlignmentMap';
import type { DocTemplate, DocDocument } from '../../types/docops';

// ── ANCHOR CONTEXT ──

/**
 * Build the anchor document alignment block.
 * Extracts relevant TOS sections and wraps them in
 * alignment rules so the generation model stays
 * consistent with the anchor document.
 */
export function buildAnchorContext(
  tosDocument: DocDocument | undefined,
  template: DocTemplate,
): string {
  if (!tosDocument || !tosDocument.content) return '';
  if (!template.anchorSections || template.anchorSections.length === 0) return '';

  const content = tosDocument.content;
  const extractedSections: string[] = [];

  for (const sectionId of template.anchorSections) {
    const extracted = extractSection(content, sectionId);
    if (extracted) {
      // Truncate each section to max 2000 chars to manage prompt size
      const truncated =
        extracted.length > 2000
          ? extracted.slice(0, 2000) + '\n[...truncated...]'
          : extracted;
      extractedSections.push(truncated);
    }
  }

  if (extractedSections.length === 0) return '';

  const lines = [
    '=== ANCHOR DOCUMENT ALIGNMENT ===',
    'This document is part of a coordinated legal library. The Terms of Service is the anchor document. The following TOS provisions are injected for alignment \u2014 your output MUST be consistent with these:',
    '',
    ...extractedSections,
    '',
    'ALIGNMENT RULES:',
    '- Entity names must match TOS exactly.',
    '- Defined terms used in TOS must be used consistently or cross-referenced.',
    '- The consent model must not contradict, expand, or narrow TOS \u00A75.',
    '- The clinical liability framework must not create liability TOS \u00A79 disclaims.',
    '- The IP ownership model must reflect the licensed-from-third-party structure.',
    '=== END ANCHOR ALIGNMENT ===',
  ];

  return lines.join('\n');
}

/**
 * Extract a section from TOS content by section ID.
 * Handles both top-level sections (e.g. '\u00A73') and
 * subsections (e.g. '\u00A713.4').
 */
function extractSection(content: string, sectionId: string): string | null {
  // Parse section ID: '\u00A73' -> sectionNum=3, sub=undefined
  // '\u00A713.4' -> sectionNum=13, sub='13.4'
  const match = sectionId.match(/\u00A7(\d+)(?:\.(\d+))?/);
  if (!match) return null;

  const mainNum = parseInt(match[1], 10);
  const subNum = match[2]; // e.g. '4' for \u00A713.4

  if (subNum) {
    // Subsection: try ### 13.4 or **13.4 first
    const subId = `${mainNum}.${subNum}`;
    const subPattern = new RegExp(
      `(?:###\\s*${escapeRegex(subId)}|\\*\\*${escapeRegex(subId)})`,
    );
    const subMatch = subPattern.exec(content);

    if (subMatch) {
      // Extract from the subsection header to the next subsection or section
      const start = subMatch.index;
      const nextSubPattern = new RegExp(
        `(?:###\\s*${mainNum}\\.\\d+|\\*\\*${mainNum}\\.\\d+|##\\s*\\d+\\.)`,
      );
      const rest = content.slice(start + subMatch[0].length);
      const nextMatch = nextSubPattern.exec(rest);
      const end = nextMatch
        ? start + subMatch[0].length + nextMatch.index
        : undefined;
      return content.slice(start, end).trim();
    }

    // Fallback: include the full parent section
    return extractTopLevelSection(content, mainNum);
  }

  // Top-level section
  return extractTopLevelSection(content, mainNum);
}

/**
 * Extract a top-level section from ## N. to ## (N+1).
 */
function extractTopLevelSection(
  content: string,
  sectionNum: number,
): string | null {
  const startPattern = new RegExp(`##\\s*${sectionNum}\\.\\s`);
  const startMatch = startPattern.exec(content);
  if (!startMatch) return null;

  const start = startMatch.index;
  const nextSectionPattern = new RegExp(`##\\s*${sectionNum + 1}\\.\\s`);
  const rest = content.slice(start + startMatch[0].length);
  const nextMatch = nextSectionPattern.exec(rest);

  const end = nextMatch
    ? start + startMatch[0].length + nextMatch.index
    : undefined;

  return content.slice(start, end).trim();
}

/**
 * Escape special regex characters in a string.
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ── DEFINED TERMS ALIGNMENT ──

/**
 * Build the defined terms alignment block for a given template.
 * Filters the master glossary to terms used in this template
 * and formats them for prompt injection.
 */
export function buildDefinedTermsPrompt(templateId: string): string {
  const applicableTerms = DEFINED_TERMS_REGISTRY.terms.filter((t) =>
    t.usedIn.includes(templateId),
  );

  if (applicableTerms.length === 0) return '';

  const termLines = applicableTerms.map(
    (t) => `- "${t.term}" = ${t.definition}`,
  );

  const lines = [
    '=== DEFINED TERMS ALIGNMENT ===',
    'When you use these terms, you MUST either (a) use the same definition, (b) state "as defined in the Terms of Service," or (c) explicitly define differently and note the distinction.',
    'NEVER silently redefine a term with a different meaning.',
    '',
    'Master defined terms:',
    ...termLines,
    '=== END DEFINED TERMS ===',
  ];

  return lines.join('\n');
}

// ── PROVISION INHERITANCE ──

/**
 * Build the provision inheritance block for a given template.
 * Filters inherited provisions to those applicable and
 * injects their prompt blocks.
 */
export function buildProvisionInheritancePrompt(templateId: string): string {
  const applicable = INHERITED_PROVISIONS.filter((p) =>
    p.appliesTo.includes(templateId),
  );

  if (applicable.length === 0) return '';

  const blocks = applicable.map((p) => p.promptBlock);

  const lines = [
    '=== PROVISION INHERITANCE ===',
    'This document inherits provisions that must appear consistently with the TOS. Include language substantively consistent with each applicable provision block.',
    '',
    ...blocks,
    '=== END PROVISION INHERITANCE ===',
  ];

  return lines.join('\n');
}

// ── CROSS-DOCUMENT SELF-CHECK ──

/**
 * Build the cross-document self-check block appended
 * after the individual quality gate self-check.
 * Includes conditional checks based on template provisions.
 */
export function buildCrossDocSelfCheck(templateId: string): string {
  // Determine which provision IDs apply to this template
  const applicableProvisionIds = INHERITED_PROVISIONS
    .filter((p) => p.appliesTo.includes(templateId))
    .map((p) => p.id);

  const lines: string[] = [
    '--- CROSS-DOCUMENT CONSISTENCY CHECK ---',
    'Before finalizing, verify cross-document alignment:',
    '',
    'ENTITY CHECK:',
    '- Every entity name matches brand config entity registry',
    '- Medical Director described as "oversight" not "supervision"',
    '- OpenLoop referenced consistently (full name on first use, short form after)',
    '',
    'DEFINED TERMS CHECK:',
    '- Every defined term matches TOS definition or cross-references TOS',
    '- No terms silently redefined',
  ];

  // Conditional checks based on inherited provisions
  if (applicableProvisionIds.includes('consent-model-reference')) {
    lines.push(
      '',
      'CONSENT MODEL CHECK:',
      '- Consent model references are consistent with TOS \u00A75',
      '- No expansion or narrowing of consent scope',
      '- Consent withdrawal rights match TOS language',
    );
  }

  if (applicableProvisionIds.includes('cpom-firewall')) {
    lines.push(
      '',
      'CPOM CHECK:',
      '- MSO/LLC does NOT practice medicine, employ physicians, or control clinical decisions',
      '- Clinical services flow through OpenLoop network, not through HemoLogix LLC',
      '- Medical Director "provides oversight" \u2014 never "supervises" OpenLoop providers',
    );
  }

  if (applicableProvisionIds.includes('ip-licensed-model')) {
    lines.push(
      '',
      'IP CHECK:',
      '- Platform Technology is licensed FROM a third-party IP owner, not owned by HemoLogix LLC',
      '- IP ownership structure reflects trust \u2192 license \u2192 company chain',
      '- No language implies HemoLogix LLC owns the IP outright',
    );
  }

  if (applicableProvisionIds.includes('clinical-liability-carveout')) {
    lines.push(
      '',
      'LIABILITY CHECK:',
      '- Clinical liability rests with OpenLoop and its affiliated providers',
      '- HemoLogix LLC disclaims clinical liability consistent with TOS \u00A79',
      '- No language creates new clinical liability for HemoLogix LLC',
    );
  }

  lines.push(
    '',
    'CROSS-REFERENCE CHECK:',
    '- Every document referenced exists or is flagged as "NOT YET GENERATED"',
    '- URLs use {{variable}} format',
    '',
    'Fix any cross-document inconsistency before completing.',
    '--- END CROSS-DOC CHECK ---',
  );

  return lines.join('\n');
}

// ── GENERATION ROUND ──

/**
 * Returns the template's generationRound, defaulting to 6 if not set.
 */
export function getGenerationRound(template: DocTemplate): number {
  return template.generationRound ?? 6;
}

// ── DEPENDENCY CHECK ──

/**
 * Check if a template's dependencies are satisfied.
 * Returns which dependencies are missing or have low audit scores.
 */
export function checkDependencies(
  template: DocTemplate,
  documents: DocDocument[],
): { ready: boolean; missing: string[]; lowScore: string[] } {
  const missing: string[] = [];
  const lowScore: string[] = [];

  if (!template.dependencies || template.dependencies.length === 0) {
    return { ready: true, missing, lowScore };
  }

  for (const depId of template.dependencies) {
    const doc = documents.find((d) => d.templateId === depId);

    if (!doc || doc.status === 'not_started') {
      missing.push(depId);
      continue;
    }

    if (doc.auditScore !== undefined && doc.auditScore < 90) {
      lowScore.push(depId);
    }
  }

  const ready = missing.length === 0 && lowScore.length === 0;
  return { ready, missing, lowScore };
}
